import KineduAuth from 'kinedu-auth';
import qs from 'query-string';
import { useState } from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import EventReporter, { getEventProviderInstance } from 'appEvents';
import { AppEvents, EventProviders } from 'constants/events';
// import EventReporter, { getEventProviderInstance } from '../appEvents';
// import { AppEvents, EventProviders } from '../constants/events';
// import { mixpanel } from '../appEvents/providers';

import { CLIENT_API_URL, ENV } from '../../../config';
import { APP_SECTIONS_PATHS } from 'constants/routes';
import { setClientAuthToken } from 'api/ClientInstance';
import { path } from 'ramda';

const { API } = KineduAuth;
const {
  login,
  signup,
  googleAuth,
  sendPasswordResetEmail,
  resetPassword: putResetPassword,
} = API(ENV, CLIENT_API_URL);

export const setLocalData = (user) => {
  const accountToken =
    user?.type === 'Teacher'
      ? user?.organization?.kinedu_classrooms_token?.access_token
      : user?.account?.kinedu_classrooms_token?.access_token;
  localStorage.setItem('user_id', user?.id);
  localStorage.setItem('account_token', accountToken);
  localStorage.setItem(
    'active_center_token',
    user?.account?.kinedu_classrooms_token?.access_token,
  );
  localStorage.setItem('account_type', user?.account?.type);
  localStorage.setItem('auth_token', user?.authentication_token);
  localStorage.setItem('user_email', user?.email);
  localStorage.setItem('user_type', user?.type);
  localStorage.setItem('user_confirmed', user?.confirmed);
  localStorage.setItem('user_locale', user?.locale);

  // If the user has organization object it means he is teacher
  let organizationId = user?.organization?.id;

  if (!organizationId) {
    organizationId = user?.account?.id;
  }

  localStorage.setItem('ORGANIZATION_ID', organizationId || '');
  localStorage.setItem('school_name', user?.account?.name);
  window.location.assign(
    `${APP_SECTIONS_PATHS.BASE_APP}${APP_SECTIONS_PATHS.DAP}`,
  );
};

const setSignupLocalData = (user) => {
  window.localStorage.setItem(
    'account_first_center_id',
    user?.account.centers[0].id,
  );
  window.localStorage.setItem('doing_ob', true);
  window.localStorage.setItem('user_confirmed', false);
};

const mixpanelRegister = (user) => {
  setClientAuthToken(user.authentication_token, user.email);
  // Let's identify the user in MIXPANEL only
  getEventProviderInstance(EventProviders.MIXPANEL).identify(user.id);

  EventReporter.setUserAttributes(user);

  if (user.organization_name) {
    localStorage.setItem('ORGANIZATION_NAME', user.organization_name);
  }

  const firstCenterId = path(['account', 'centers', 0, 'id'], user);

  localStorage.setItem('account_first_center_id', firstCenterId);

  EventReporter.action(AppEvents.LOGIN(), [EventProviders.MIXPANEL]);
};

const useAuth = () => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({
    emailError: '',
    passError: '',
    generalError: '',
  });

  const onLogin = ({ email, password }) => {
    const loginData = qs.stringify({
      email,
      password,
    });

    return login(loginData)
      .then(({ user, data }) => {
        user = {
          ...(user || {}),
          ...(data?.user || {}),
        };
        mixpanelRegister(user);
        setLocalData(user);
        return user;
      })
      .catch((err) => {
        if (err.response) {
          if (err.response.data.error === 'Your account is locked.') {
            setErrors({
              generalError: 'Your account is locked.',
            });
          } else {
            const generalError =
              err.response.data.error || 'Something went wrong';
            const authError =
              err.response.data.error ===
                'Invalid %{authentication_keys} or password.' &&
              t('auth:ERROR_LOGIN');
            const emailError =
              err.response.data.email && err.response.data.email[0];
            const passError =
              err.response.data.password && err.response.data.password[0];
            console.log(
              '%c🚫 err',
              'background: #a20300; color: #ffa8a6; font-size: 16px',
              err.response,
            );
            setErrors({
              emailError,
              passError,
              generalError: authError || generalError,
            });
          }
        }

        return new Error(err);
      });
  };

  const onSignup = ({ email, password, name = 'Organization name' }) =>
    new Promise((resolve, reject) => {
      signup({
        user: {
          email,
          password,
          locale: i18n.language,
          organization_attributes: {
            name,
          },
        },
      })
        .then(({ data }) => {
          const { user } = data;
          mixpanelRegister(user);
          setLocalData(user);
          setSignupLocalData(user);
          EventReporter.action(AppEvents.SIGNUP(), [EventProviders.MIXPANEL]);
          resolve({
            user,
          });
          return user;
        })
        .catch((err) => {
          let generalError = err.response.data.error || 'Something went wrong';
          let emailError = err.response.data.email
            ? err.response.data.email[0]
            : '';

          if (emailError === 'has already been taken') {
            emailError = 'auth:ERROR_ALREADY_AN_ACCOUNT';
            generalError = '';
          }
          const passError = err.response.data.password
            ? err.response.data.password[0]
            : '';
          setErrors({
            emailError,
            passError,
            generalError,
          });
          console.log(
            '%c🚫 err',
            'background: #a20300; color: #ffa8a6; font-size: 16px',
            err.response.data,
          );

          reject(err);
          return new Error(err);
        });
    });

  const sendResetPasswordEmail = (email) =>
    new Promise((resolve, reject) => {
      sendPasswordResetEmail({
        user: { email },
      })
        .then((response) => {
          resolve(response);
        })
        .catch((err) => {
          console.log(
            '%c🐛 err',
            'background: #332167; color: #b3d1f6; font-size: 11px',
            err,
          );
          const generalError =
            err.response.data.error || 'Something went wrong';

          setErrors({
            generalError,
          });
          reject(err);
        });
    });

  const resetPassword = (
    token,
    password,
    confirmPassword,
    view,
    schoolName,
  ) => {
    putResetPassword(token, password, confirmPassword)
      .then(({ user }) => {
        setLocalData(user);
        localStorage.setItem(view, schoolName);
      })
      .catch((err) => {
        localStorage.setItem(view, 'error');
        const generalError =
          err.response.data.reset_password_token[0] === 'is invalid'
            ? 'Invalid token'
            : 'Something went wrong';
        setErrors({
          generalError,
        });
      });
  };

  const onGoogleAuth = (accessToken, googleUser) => {
    googleAuth(accessToken)
      .then(({ data }) => {
        const { user } = data;
        mixpanelRegister(user);
        setLocalData(user);
        if (user?.new_user) {
          setSignupLocalData(user);
        }
      })
      .catch((error) => {
        console.info(error);
      });
  };

  return {
    onLogin,
    onSignup,
    onGoogleAuth,
    errors,
    setErrors,
    sendResetPasswordEmail,
    resetPassword,
  };
};

export default useAuth;
